import React, { DragEvent } from "react";
import styled from "styled-components";
import {
  Box,
  Flex,
  Icon,
  styles,
  Text,
  useWindowSize,
  useStoredState,
} from "@thenounproject/lingo-core";

import { InsertType } from "../../constants/InsertType";

type PanelWrapperProps = {
  isOpen: boolean;
  panelPeak: number;
};

const TAB_WIDTH = 16;
const INSERT_PANEL_WIDTH = 54;
const SMALL_BREAKPOINT = styles.bp.s;
const ANIMATION_OVERSHOOT = 6;
const DRAG_ANIMATION_OFFSET = 8;
const HOVER_THRESHOLD_PX = -8;

const InsertPanelWrapper = styled(Flex).attrs<PanelWrapperProps>(props => {
  return {
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "flex-end",
    background: "transparent",
    top: 0,
    left: 0,
    height: "100%",
    position: "absolute",
    zIndex: 100,
    maxHeight: "100vh",
    overflow: "hidden",
    width: TAB_WIDTH + TAB_WIDTH + (props.isOpen ? INSERT_PANEL_WIDTH : props.panelPeak),
    pr: TAB_WIDTH,
    transitionDelay: "0.1s",
    transition: props.isOpen
      ? "width 0.3s cubic-bezier(.42,.14,.25,1.3)"
      : "width 0.2s cubic-bezier(.02,.22,.55,1)",
  };
})<PanelWrapperProps>``;

const InsertPanelContentWrapper = styled(Flex).attrs({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "grayLightest",
  borderTop: "default",
  borderBottom: "default",
  borderRight: "default",
  gap: "16px",
  py: "16px",
  width: INSERT_PANEL_WIDTH + ANIMATION_OVERSHOOT,
  pl: ANIMATION_OVERSHOOT,
  borderRadius: "0 4px 4px 0",
  position: "relative",
  mr: TAB_WIDTH,
})``;

const PanelItem = styled(Flex).attrs({
  flexShrink: 0,
  borderRadius: "default",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  width: "32px",
  height: "32px",
  mb: "s",
  mt: "xs",
  background: "white",
  boxShadow: "button",
})`
  transition-property: "transform, margin-right";
  transition-timing-function: ease-out;
  transition-duration: 0.1s;

  cursor: pointer;
  svg {
    margin: 0;
  }
  &[draggable="true"] {
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
    &:hover {
      margin-right: -${DRAG_ANIMATION_OFFSET}px;
    }
  }
`;

const TabButton = styled(Flex).attrs({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: TAB_WIDTH,
  height: "60px",
  backgroundColor: "grayLightest",
  borderTop: "default",
  borderBottom: "default",
  borderRight: "default",
  position: "absolute",
  variations: {
    "&:hover": {
      backgroundColor: "grayLighter",
      position: "absolute",
    },
  },
  borderRadius: "0 4px 4px 0",
})`
  position: absolute;
  z-index: 500;
  top: 50%;
  transform: translateY(-50%);
  right: ${-TAB_WIDTH}px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
`;

type ItemWrapperProps = {
  divider?: boolean;
  opacity?: number;
  transitionDelay?: string;
};
const PanelItemWrapper = styled(Flex).attrs<ItemWrapperProps>(() => {
  return {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  };
})<ItemWrapperProps>`
  opacity: ${props => props.opacity ?? 1};
  transition: opacity 0.4s ease-in;
  transition-delay: ${props => props.transitionDelay ?? "0s"};
`;

const panelItems = [
  {
    label: "Asset",
    tooltip: "Drag to add assets",
    icon: "content.file",
    type: InsertType.assets,
    divider: true,
  },
  {
    label: "Heading",
    tooltip: "Drag to add a heading",
    icon: "content.heading",
    type: InsertType.heading,
  },
  {
    label: "Note",
    tooltip: "Drag to add a note",
    icon: "content.note",
    type: InsertType.note,
    divider: true,
  },
  {
    label: "Banner",
    tooltip: "Drag to add a banner",
    icon: "content.supporting-image",
    type: InsertType.supportContent,
  },
  {
    label: "Guide",
    tooltip: "Drag to add guidelines",
    icon: "content.guide",
    type: InsertType.guide,
    divider: true,
  },
  {
    label: "Gallery",
    tooltip: "Drag to add a Grid",
    icon: "content.grid",
    type: InsertType.gallery,
  },
];

type Props = {
  emptySection?: boolean;
};

const InsertPanel: React.FC<Props> = ({ emptySection }) => {
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= SMALL_BREAKPOINT;

  const [lockedOpen, setLockedOpen] = useStoredState<boolean>("keepInsertPanelOpen", !isMobile);
  const [isHovered, setIsHovered] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const rectRef = React.useRef<DOMRect | null>(null);

  const insertPanelPeak = isMobile ? 0 : 10;
  const isOpen = lockedOpen || isHovered || emptySection;

  // Handle mouse movement with threshold for hover behavior
  const handleMouseMove = React.useCallback(
    (event: React.MouseEvent) => {
      if (!rectRef.current) return;

      const mouseX = event.clientX;
      const relativeX = rectRef.current.right - mouseX;

      if (relativeX >= HOVER_THRESHOLD_PX) {
        if (!isHovered) {
          setIsHovered(true);
        }
      } else {
        if (isHovered) {
          setIsHovered(false);
        }
      }
    },
    [isHovered]
  );

  // Cache the panel's bounding rect for performance in handleMouseMove
  React.useEffect(() => {
    if (panelRef.current) {
      rectRef.current = panelRef.current.getBoundingClientRect();
    }
  }, [isOpen, isMobile]);

  // Handle click outside to close on mobile
  React.useEffect(() => {
    if (!isMobile || !lockedOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setLockedOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, lockedOpen, setLockedOpen]);

  function handleDragStart(e: DragEvent, insertType: InsertType) {
    setIsHovered(false);
    // Close panel on mobile when dragging starts
    if (isMobile) {
      setLockedOpen(false);
    }
    e.dataTransfer.setData("insertType", insertType);
  }

  function handleDragEnd(e: DragEvent) {
    e.preventDefault();
  }

  const renderPanelItems = () => {
    return panelItems.map(p => {
      const labelId = "panel-item-".concat(p.type.split(" ").join("-").toLowerCase());
      return (
        <PanelItemWrapper
          divider={p.divider}
          key={labelId}
          opacity={isOpen ? 1 : 0}
          transitionDelay={isOpen ? "0" : "0.1s"}>
          <PanelItem
            data-testid={labelId}
            data-tooltip-source={labelId}
            onDragStart={e => handleDragStart(e, p.type)}
            onDragEnd={handleDragEnd}
            draggable="true">
            <Icon iconId={p.icon} fill="black" />
          </PanelItem>
          <Text font="ui.tiny" color="grayDarkest" textAlign="center">
            {p.label}
          </Text>
        </PanelItemWrapper>
      );
    });
  };

  const tabIcon = (() => {
    if (isMobile) return "action.add";
    return lockedOpen ? "navigation.chevron-left" : "navigation.chevron-right";
  })();

  return (
    <InsertPanelWrapper
      className="intercom-insert-panel"
      isOpen={isOpen}
      panelPeak={insertPanelPeak}>
      <Box>
        <InsertPanelContentWrapper
          ref={panelRef}
          onMouseMove={!isMobile ? handleMouseMove : undefined}
          onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}>
          {!emptySection && (
            <TabButton
              onClick={() => {
                const open = !lockedOpen;
                setLockedOpen(open);
                if (!open) {
                  setIsHovered(false);
                }
              }}>
              <Icon size="16px" iconId={tabIcon} fill="grayDarker" />
            </TabButton>
          )}
          {renderPanelItems()}
        </InsertPanelContentWrapper>
      </Box>
    </InsertPanelWrapper>
  );
};

export default InsertPanel;
